<template>
    <row v-if="done">
      <col6>
        <v-card class="elevation-4">
            <v-card-title>
                {{$t('annotate.no_ann_title')}}
            </v-card-title>
            <v-card-subtitle style="min-height: 60px;">
                {{$t('annotate.no_ann_subtitle')}}
            </v-card-subtitle>
           <v-card-text>
              <v-img :src="api_url + '/digger.png'"></v-img>
           </v-card-text>
        </v-card>
      </col6>

    </row>
    <div
      v-else
      class="annotate-layout"
      :data-asset-id="asset_id"
      :data-preloaded-asset-id="preloadedAssets.length ? preloadedAssets[0].asset_id : null"
      :data-preloaded-count="preloadedAssets.length"
    >
      <div class="annotate-layout__main">
        <loader :loading="loading"></loader>
        <div v-if="annotationLimitReached">
          <show-alert :alert-message="annotationLimitReachedMessage"></show-alert>
        </div>
        <div id="dummy" tabindex="-1" style="width: 0; height: 0; overflow: hidden;"></div>
        <asset-messages-panel
          :messages="asset_messages"
          :title="$t('annotate.asset_messages_title')"
          :system-label="$t('annotate.asset_messages_system')"
          :user-label="$t('annotate.asset_messages_user')"
          :archive-label="$t('annotate.asset_messages_archive')"
          @archive="archiveAssetMessage"
        />
        <message-composer
          v-if="asset_id"
          :asset-id="asset_id"
          @added="onAssetMessageAdded"
        />
        <annotated-image
            @image_loaded="refresh_annotations"
            @canvas_mousedown="canvas_mousedown"
            :is_embedding_loading="isSam3Loading"
            :zoom_enabled="true"
            :show_brightness_contrast="true"
            :sensitive-blur="sensitiveBlurForCurrentAsset"
            :image_url="asset_url"
            :annotations="annotations"
            :placement_label="pendingPlacement"
            @place_on_canvas="onPlaceOnCanvas"
            @update_coords="updateAnnotation"
            @delete_annotation="deleteAnnotation"
            @select="selectAnnotation"
            @unselect="unselectAnnotation"
            @add_annotation_deselect_others="add_annotation_deselect_others"
            @update_annotation_label="updateAnnotationLabel"
            v-model:contrast="contrast"
            v-model:brightness="brightness"
        ></annotated-image>
        <meta-grid
          v-if="asset_id != null && asset_id !== ''"
          :meta="meta"
          :asset-id="asset_id"
          @meta-updated="onMetaUpdated"
        ></meta-grid>
      </div>
      <aside class="annotate-sidebar">
          <section v-if="showLlmAnnotateAiTools" class="annotate-sidebar__section">
            <v-switch
                v-model="auto_boxes_enabled"
                :label="$t('annotate.auto_boxes')"
                color="primary"
                :disabled="annotationLimitReached"
                inset
                hide-details
                density="compact"
            ></v-switch>
            <template v-if="auto_boxes_enabled">
              <v-text-field
                  v-model="auto_boxes_query"
                  :label="$t('annotate.auto_boxes_hint')"
                  density="compact"
                  variant="outlined"
                  :disabled="annotationLimitReached || !asset_id"
                  @keyup.enter="runAutoBoxes"
              ></v-text-field>
              <v-btn
                  color="primary"
                  block
                  class="mb-2"
                  :disabled="annotationLimitReached || !asset_id || auto_boxes_loading"
                  :loading="auto_boxes_loading"
                  @click="runAutoBoxes"
              >
                {{$t('annotate.auto_boxes_run')}}
              </v-btn>
              <div v-if="auto_boxes_status_updates && auto_boxes_status_updates.length > 0" class="annotate-sidebar__status">
                <div v-for="(status, idx) in auto_boxes_status_updates" :key="'auto-box-status-' + idx">
                  {{ status }}
                </div>
              </div>
            </template>
          </section>

          <section v-if="show_suggest_text_tools" class="annotate-sidebar__section">
            <v-btn
                class="mb-2 mr-2"
                color="primary"
                variant="outlined"
                size="small"
                :disabled="annotationLimitReached || !annotate_asset_id || text_boxes_loading"
                :loading="text_boxes_loading"
                @click="runSuggestTextBoxes"
            >
              {{$t('annotate.suggest_text_boxes')}}
            </v-btn>
            <v-btn
                class="mb-2"
                color="primary"
                variant="outlined"
                size="small"
                :disabled="annotationLimitReached || !annotate_asset_id || text_boxes_loading"
                :loading="text_boxes_loading"
                @click="runSuggestTextRecognition"
            >
              {{$t('annotate.suggest_text_recognition')}}
            </v-btn>
            <div v-if="text_boxes_status_updates && text_boxes_status_updates.length > 0" class="annotate-sidebar__status">
              <div v-for="(status, idx) in text_boxes_status_updates" :key="'text-box-status-' + idx">
                {{ status }}
              </div>
            </div>
            <text-suggestions-panel
                class="mt-2"
                :suggestions="text_suggestions"
                :loading="text_suggestions_loading"
                @accept="acceptTextSuggestions"
                @reject="rejectTextSuggestions"
                @accept-all="acceptAllTextSuggestions"
                @reject-all="rejectAllTextSuggestions"
            />
          </section>

          <section v-if="sam3_enabled" class="annotate-sidebar__section">
            <div class="annotate-sidebar__heading">{{ $t('annotate.sam3') }}</div>
            <div class="annotate-sidebar__hint">{{ $t('annotate.sam3_click_hint') }}</div>
            <v-text-field
                v-model="sam3_query"
                :label="$t('annotate.sam3_hint')"
                density="compact"
                variant="outlined"
                :disabled="annotationLimitReached || !asset_id || sam3_loading"
                @keyup.enter="runSam3TextSegment"
            ></v-text-field>
            <v-btn
                color="primary"
                block
                class="mb-2"
                :disabled="annotationLimitReached || !asset_id || sam3_loading"
                :loading="sam3_loading"
                @click="runSam3TextSegment"
            >
              {{$t('annotate.sam3_run')}}
            </v-btn>
            <div v-if="sam3_status" class="annotate-sidebar__status">
              {{ sam3_status }}
            </div>
          </section>

          <section class="annotate-sidebar__section">
            <text-annotate-panel
                :annotations="annotations"
                :selected-id="selected_annotation_id"
                @update-transcript="updateAnnotationTranscript"
            />
          </section>

          <v-divider class="my-3" />

          <section class="annotate-sidebar__section">
            <annotate-label-placement
                :disabled="annotationLimitReached"
                :pending-placement="pendingPlacement"
                @label_selected="onLabelSelectedForPlacement"
                @clear="clearPendingPlacement"
            ></annotate-label-placement>
            <annotate-label-groups
                @add_annotation="addAnnotationClearSelectionAndClearFocus"
                :type="'group'"
            ></annotate-label-groups>
          </section>

          <v-divider class="my-3" />

          <section class="annotate-sidebar__section">
            <div class="annotate-sidebar__actions">
              <v-btn v-if="hasAnnotationSuggestions && show_annotation_suggestions" @click="show_suggestions" color="primary" size="small" id="annotation_suggestion">
                {{$t('annotate.btn_show')}}
              </v-btn>
              <v-btn v-if="!show_annotation_suggestions" @click="hide_suggestions" color="primary" size="small" id="annotation_suggestion">
                {{$t('annotate.btn_hide')}}
              </v-btn>
              <suggestion-match-log-info :logs="suggestion_match_logs" />
              <v-btn v-if="hasEncodedBoundingBoxes && !show_bounding_box_meta" @click="show_bbox_meta" color="primary" size="small" id="show_bounding_box_meta">
                {{$t('annotate.btn_show_meta')}}
              </v-btn>
              <v-btn v-if="show_bounding_box_meta" @click="hide_bbox_meta" color="primary" size="small" id="hide_bounding_box_meta">
                {{$t('annotate.btn_hide_meta')}}
              </v-btn>
            </div>
            <annotations-list
                @update_label="updateAnnotationLabel"
                @clear="clearAnnotations"
                @select="selectAnnotation"
                @unselect="unselectAnnotation"
                :annotations="annotations"
            ></annotations-list>
          </section>

          <v-divider class="my-3" />

          <div v-if="original_category" class="annotate-sidebar__meta text-caption mb-3">
            Original category:<br />
            {{ original_category }}
          </div>

          <annotate-buttons
              :instant_advance="true"
              @set_loading="set_loading"
              :asset_id="asset_id"
              :annotations="annotations"
              :asset_id_history="asset_id_history"
              @store_action="onStoreAction"
              @check_previous="openPreviousAssetModal"
              @annotate-limit-reached="handleAnnotationLimitReached"
          ></annotate-buttons>
      </aside>
      <annotate-modal
          v-if="previous_asset_modal && previous_asset_id"
          :asset_id="previous_asset_id"
          @close_modal="closePreviousAssetModal"
          @remove_item="closePreviousAssetModal"
      ></annotate-modal>
    </div>
</template>

<script>

import AnnotatedImage from "@/components/AnnotatedImage";
import AnnotateMixin from "@/mixins/AnnotateMixin";
import Annotation from "@/classes/annotation";
import AnnotationsList from "@/components/AnnotationsList";
import AnnotateLabelPlacement from "@/components/AnnotateLabelPlacement.vue";
import AnnotateButtons from "@/components/AnnotateButtons";
import AnnotateModal from "@/components/AnnotateModal";
import AnnotateLabelGroups from "@/components/AnnotateLabelGroups";
import Row from "@/components/layout/Row";
import Col6 from "@/components/layout/Col6";
import LoadSettingsMixin from "@/mixins/LoadSettingsMixin";
import ShowAlert from "@/components/ShowAlert.vue";
import LoadProjectsMixin from "@/mixins/LoadProjectsMixin";
import CurrentUserMixin from "@/mixins/CurrentUserMixin";
import Loader from "@/components/layout/loader";
import MetaGrid from "@/components/MetaGrid";
import TextAnnotatePanel from "@/components/TextAnnotatePanel.vue";
import TextSuggestionsPanel from "@/components/TextSuggestionsPanel.vue";
import AssetMessagesPanel from "@/components/AssetMessagesPanel.vue";
import MessageComposer from "@/components/MessageComposer.vue";
import SuggestionMatchLogInfo from "@/components/SuggestionMatchLogInfo.vue";
// import SegAnyMixin from "@/mixins/SegAnyMixin";
import Sam3Mixin from "@/mixins/Sam3Mixin";
import {
  getBlurUnlessMetaFromSettings,
  shouldBlurAssetMeta,
} from '@/utils/sensitiveImageBlur';
import {
  ANNOTATE_PRELOAD_COUNT,
  buildAssetStateFromNextResult,
  isExcludedAssetId,
  preloadImage,
} from '@/utils/annotatePreload';


export default {
  name: "Annotate",
  components: {
    Col6,
    Row,
    AnnotateLabelGroups, Loader, AnnotateButtons, AnnotateModal, AnnotateLabelPlacement, AnnotationsList, AnnotatedImage, ShowAlert, MetaGrid, TextAnnotatePanel, TextSuggestionsPanel, AssetMessagesPanel, MessageComposer, SuggestionMatchLogInfo
  },
  mixins: [AnnotateMixin, LoadSettingsMixin, LoadProjectsMixin, CurrentUserMixin, Sam3Mixin],
  data() {
    return {
      'loading': true,
      'canvas_id': 'custom_id',
      'find_meta_contains': '',
      'find_original_category': '',
      'original_category': '',
      'meta': '',
      'asset_id': null,
      'asset_id_history': [],
      'asset_url': null,
      'brightness': 0,
      'contrast': 0,
      'show_filter': false,
      'max_results': false,
      'done': false,
      'show_annotation_suggestions': true,
      'show_bounding_box_meta' : false,
      'annotationLimitReached': false,
      'show_meta': false,
      'previous_asset_modal': false,
      'previous_asset_id': null,
      'preloadedAssets': [],
      'preloadGeneration': 0,
      'pendingBackgroundAssetIds': [],
    }
  },
  mounted() {
    this.next();
  },
  props: {
    'filters': {required: true},
    'processing_tasks': {required: false, default: false},
  },
  computed: {
    annotationLimitReachedMessage() {
      return this.getLimitMessage('annotate', 'ANNOTATION_LIMIT');
    },
    contact_email() {
      return 'Contact email'//TODO$('#contact_email').val();
    },
    api_url () {
      return this.$api_url;
    },
    hasAnnotationSuggestions() {
      return this.annotation_suggestions.length > 0;
    },
    sensitiveBlurForCurrentAsset() {
      const policy = getBlurUnlessMetaFromSettings(this.settings || []);
      return shouldBlurAssetMeta(this.meta, policy, this.is_sensitive_image_blur_exempt);
    },
  },
  watch: {
    filters() {
      this.invalidatePreload();
      this.next();
    }
  },
  methods: {
    onMetaUpdated(metaStr) {
      this.meta = metaStr;
    },
    handleAnnotationLimitReached() {
      this.annotationLimitReached = true
    },
    async getUserAnnotationsCount() {
      const response = await this.$api.annotation.filter([], 1000000);
      if (response) {
        return response.items.length;
      }
    },
    set_loading(loading) {
      this.loading = loading;
    },
    openPreviousAssetModal(asset_id) {
      if (!asset_id) {
        return;
      }
      this.previous_asset_id = asset_id;
      this.previous_asset_modal = true;
    },
    closePreviousAssetModal() {
      this.previous_asset_modal = false;
      this.previous_asset_id = null;
    },
    refresh_annotations() {
      this.annotations = this.annotations.slice();
    },
    updateFilterData(filter_data) {
      this.filter_data = filter_data
    },
    refresh() {
      this.next();
    },
    isEmptyNextAssetResult(result) {
      return !result || result.done || result.error || !result.asset;
    },
    finishAnnotateQueue(processing_tasks) {
      this.$emit('update_progress', 0);
      this.loading = false;
      if (!processing_tasks) {
        this.done = true;
      }
    },
    getAnnotateFilters() {
      if (this.filters) {
        return this.filters;
      }
      return JSON.stringify([{'type': 9, 'value': 'No'}, {'type': 61, 'value': 'No'}]);
    },
    getExcludeAssetIds() {
      return [...new Set([
        this.asset_id,
        ...this.pendingBackgroundAssetIds,
        ...this.preloadedAssets.map((state) => state.asset_id),
      ].filter(Boolean))];
    },
    invalidatePreload() {
      this.preloadedAssets = [];
      this.preloadGeneration += 1;
    },
    updateProgressCount() {
      this.$api.asset.count_filter(JSON.parse(this.getAnnotateFilters())).then((count) => {
        this.$emit('update_progress', count);
      });
    },
    buildAssetStateFromResult(result) {
      return buildAssetStateFromNextResult(result, this.$api_host, Annotation);
    },
    async fetchNextAssetState(excludeAssetIds = []) {
      const filters = this.getAnnotateFilters();
      const exclude = [...new Set(excludeAssetIds.filter(Boolean))];

      let result = await this.$api.asset.next_asset(filters, null, exclude);
      if (this.isEmptyNextAssetResult(result)) {
        result = await this.$api.asset.next_asset(filters);
      }

      if (this.isEmptyNextAssetResult(result)) {
        return null;
      }

      if (!isExcludedAssetId(result.asset.id, exclude)) {
        return this.buildAssetStateFromResult(result);
      }

      return null;
    },
    async applyAssetState(state) {
      this.brightness = 0;
      this.contrast = 0;
      this.show_annotation_suggestions = true;
      this.show_bounding_box_meta = false;
      this.pendingPlacement = null;
      this.sam3_status = null;
      this.sam3_loading = false;
      this.annotations = [];
      this.asset_id = state.asset_id;
      this.asset_url = state.asset_url;
      this.meta = state.meta;
      this.original_category = state.original_category;
      this.annotation_suggestions = state.annotation_suggestions;
      this.suggestion_match_logs = state.suggestion_match_logs || [];
      this.asset_messages = state.asset_messages || [];
      this.annotations = state.annotations;
      this.initial_annotations = state.initial_annotations;

      if (this.hasAnnotationSuggestions) {
        await this.show_suggestions();
      }
    },
    async preloadNextAsset(retryCount = 0) {
      if (!this.asset_id || this.done) {
        return;
      }

      const generation = this.preloadGeneration + 1;
      this.preloadGeneration = generation;

      try {
        while (this.preloadedAssets.length < ANNOTATE_PRELOAD_COUNT) {
          if (generation !== this.preloadGeneration) {
            return;
          }

          const state = await this.fetchNextAssetState(this.getExcludeAssetIds());
          if (!state) {
            if (this.preloadedAssets.length === 0 && retryCount < 3) {
              setTimeout(() => this.preloadNextAsset(retryCount + 1), 2000);
            }
            break;
          }

          await preloadImage(state.asset_url, localStorage.token);
          if (generation !== this.preloadGeneration) {
            return;
          }

          this.preloadedAssets.push(state);
        }
      } catch (e) {
        // Preload is best-effort; retry briefly before falling back to on-demand load.
        if (retryCount < 3) {
          setTimeout(() => this.preloadNextAsset(retryCount + 1), 2000);
        }
      }
    },
    onStoreAction(payload) {
      const snapshot = {
        ...payload,
        annotations: JSON.parse(JSON.stringify(payload.annotations)),
      };
      this.runBackgroundStoreAction(snapshot);
      this.next({ usePreload: true });
    },
    async runBackgroundStoreAction(payload) {
      const { action, asset_id, annotations, asset_in_validation_set } = payload;
      this.pendingBackgroundAssetIds.push(asset_id);

      try {
        if (action === 'save') {
          const response = await this.$api.asset.save(asset_id, annotations, asset_in_validation_set);
          if (response?.limit_reached && response?.limit_type === 'ANNOTATION_LIMIT') {
            this.handleAnnotationLimitReached();
          }
        } else if (action === 'invalid') {
          await this.$api.asset.mark_invalidated([asset_id]);
        } else if (action === 'unclear') {
          await this.$api.asset.mark_unclear(asset_id);
        } else if (action === 'skipped') {
          await this.$api.asset.mark_skipped(asset_id);
        }
      } catch (e) {
        console.error('Background annotate action failed', e);
      } finally {
        this.pendingBackgroundAssetIds = this.pendingBackgroundAssetIds.filter((id) => id !== asset_id);
      }
    },
    async next(options = {}) {
      const { usePreload = false } = options;
      this.done = false;

      if (this.asset_id) {
        this.asset_id_history.push(this.asset_id);
      }

      const processing_tasks = !!this.filters;

      if (usePreload && this.preloadedAssets.length > 0) {
        const preloaded = this.preloadedAssets.shift();
        this.preloadGeneration += 1;

        await this.applyAssetState(preloaded);
        this.updateProgressCount();
        this.preloadNextAsset();
        return;
      }

      this.loading = true;
      this.brightness = 0;
      this.contrast = 0;
      this.invalidatePreload();

      const state = await this.fetchNextAssetState(this.getExcludeAssetIds());

      if (!state) {
        this.finishAnnotateQueue(processing_tasks);
        return;
      }

      this.updateProgressCount();
      await this.applyAssetState(state);
      this.loading = false;
      this.preloadNextAsset();
    },
    addSuggestionAnnotation(annotation) {
      this.addAnnotationClearSelection(annotation);
      document.getElementById('dummy').focus();
    },
    addAnnotationClearSelectionAndClearFocus(annotation) {
      this.addSuggestionAnnotation(annotation);
    },
    hide_bbox_meta() {
      this.annotations = JSON.parse(JSON.stringify(this.initial_annotations));
      this.show_bounding_box_meta = false;
    },
  }
}
</script>

<style scoped>
.annotate-layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(260px, 1fr);
  gap: 0 12px;
  align-items: start;
  width: 100%;
}

@media (max-width: 959px) {
  .annotate-layout {
    grid-template-columns: 1fr;
  }
}

.annotate-layout__main {
  min-width: 0;
  width: 100%;
}

.annotate-layout__main :deep(.annotated-image-root) {
  width: 100%;
  max-width: 100%;
}

.annotate-sidebar {
  position: sticky;
  top: 72px;
  max-height: calc(100vh - 80px);
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  padding: 0 8px 16px 12px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}

/* v-row uses negative horizontal margins; breaks narrow sidebar columns */
.annotate-sidebar :deep(.v-row) {
  margin-inline: 0;
  max-width: 100%;
}

.annotate-sidebar :deep(.v-col) {
  padding-inline: 0;
  max-width: 100%;
}

.annotate-sidebar :deep(.v-card-text) {
  padding-inline: 0;
}

.annotate-sidebar :deep(.v-chip) {
  max-width: 100%;
  white-space: normal;
  height: auto;
  min-height: 32px;
}

.annotate-sidebar__section + .annotate-sidebar__section {
  margin-top: 12px;
}

.annotate-sidebar__heading {
  font-weight: 600;
  margin-bottom: 4px;
}

.annotate-sidebar__hint,
.annotate-sidebar__status,
.annotate-sidebar__meta {
  font-size: 12px;
  opacity: 0.85;
}

.annotate-sidebar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.image-wrapper {
  position: relative;
  display: inline-block;
}

.mask-overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}
</style>
